/**
 * Validation for work assignment, general test execution, the minimal
 * equipment register, the specification register and record attachments.
 *
 * READ CLAUDE.md SECTION 4 FIRST. The general result schema below is the
 * complete general path: a summary in each language, general notes and two
 * execution dates. There is deliberately no schema here for a reading, a unit,
 * a formula, a rounding rule, an acceptance limit, a pass/fail flag, a chart or
 * an Excel mapping — for any test, including the five initial ones.
 *
 * THE ATTEMPT SCHEMAS ARE STRICT. An unknown key is refused with a 400 rather
 * than silently dropped, so a client that sends `status: 'approved'`,
 * `performedBy` or a `readings` array learns immediately that no such thing
 * can be written here — it is not quietly ignored while looking accepted.
 *
 * The backend re-validates every rule. The frontend uses these so a person
 * hears about a problem before the round trip.
 */
import { z } from 'zod';
import { ALL_ASSIGNMENT_LIST_VIEWS, ALL_ATTEMPT_ATTACHMENT_PURPOSES, ALL_EQUIPMENT_ROLES, ALL_EQUIPMENT_STATUSES, D2216_METHODS, EQUIPMENT_STATUS, T96_FRACTION_KEYS, } from '@lims/shared-types';
import { boundedText, isoDateSchema, isoTimestampSchema, listQuerySchema, reasonSchema, uuidSchema, } from './primitives.js';
import { workOrderPrioritySchema } from './work-orders.js';
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    // An empty string is an absent value, not a value of "". Storing "" would
    // make an empty field look like a recorded fact (`CLAUDE.md` section 8).
    .transform((value) => (value === '' ? null : value));
const booleanQuery = z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true');
export const equipmentStatusSchema = z.enum(ALL_EQUIPMENT_STATUSES);
export const assignmentListViewSchema = z.enum(ALL_ASSIGNMENT_LIST_VIEWS);
export const attemptAttachmentPurposeSchema = z.enum(ALL_ATTEMPT_ATTACHMENT_PURPOSES);
// ---------------------------------------------------------------------------
// Equipment register (D85)
// ---------------------------------------------------------------------------
/**
 * Register a piece of equipment.
 *
 * The code is the laboratory's OWN asset number, typed in rather than issued,
 * because a laboratory's instruments are already tagged before any software
 * arrives. Nothing here says which tests an instrument serves: that is not
 * known (limitation 26).
 */
/**
 * The descriptive register fields Phase 11 adds (D156). Every one optional and
 * nullable: a laboratory records what it knows about an instrument, and an
 * empty field is "not recorded", never a value. The responsible person must be
 * an active member of this laboratory — checked by the API
 * (`responsible_user_not_member`), which alone knows the membership.
 */
const equipmentRegisterShape = {
    equipmentType: optionalText(200),
    manufacturer: optionalText(200),
    model: optionalText(200),
    serialNumber: optionalText(200),
    locationText: optionalText(200),
    responsibleUserId: uuidSchema.nullable().optional(),
};
export const createEquipmentSchema = z
    .object({
    equipmentCode: boundedText(1, 64),
    /** ONE name (D183). */
    name: boundedText(1, 200),
    departmentId: uuidSchema.nullable().optional(),
    /** Omitted means the backend's default applies. */
    calibrationRequired: z.boolean().optional(),
    calibrationValidUntil: isoDateSchema.nullable().optional(),
    notes: optionalText(2000),
    ...equipmentRegisterShape,
})
    .strict();
/** Correct the register entry. Status and calibration have their own acts. */
export const updateEquipmentSchema = z
    .object({
    equipmentCode: boundedText(1, 64).optional(),
    name: boundedText(1, 200).optional(),
    departmentId: uuidSchema.nullable().optional(),
    calibrationRequired: z.boolean().optional(),
    notes: optionalText(2000),
    ...equipmentRegisterShape,
})
    .strict();
export const setEquipmentStatusSchema = z
    .object({
    status: equipmentStatusSchema,
    reason: reasonSchema.optional(),
})
    .strict()
    .refine((value) => value.status === EQUIPMENT_STATUS.IN_SERVICE || Boolean(value.reason), { message: 'Taking equipment out of service requires a reason.', path: ['reason'] });
/**
 * Record the date a calibration is valid until.
 *
 * `null` records that no valid calibration is known. It never means
 * "calibrated indefinitely".
 */
export const setEquipmentCalibrationSchema = z
    .object({
    calibrationValidUntil: isoDateSchema.nullable(),
    note: optionalText(2000),
})
    .strict();
export const equipmentListQuerySchema = listQuerySchema.extend({
    status: equipmentStatusSchema.optional(),
    departmentId: uuidSchema.optional(),
    /** Only equipment usable today, in the laboratory's timezone. */
    usableOnly: booleanQuery,
    /**
     * Phase 11 (D156): instruments whose calibration is valid only until this
     * date or earlier — or which need one and have none recorded. Retired
     * instruments are excluded. A filter the CALLER dates, not a stored
     * "due soon" policy: how early a laboratory wants warning is its own choice.
     */
    calibrationDueBy: isoDateSchema.optional(),
    responsibleUserId: uuidSchema.optional(),
});
// ---------------------------------------------------------------------------
// Calibration history and certificates (Phase 11, D156)
// ---------------------------------------------------------------------------
/**
 * Record a calibration in the instrument's append-only history.
 *
 * `calibratedOn` is when it was performed; a date after the laboratory's today
 * is refused by the API (`calibrated_on_in_future`). `validUntil` is REQUIRED
 * and nullable: `null` states that no valid calibration is known, and is never
 * "valid indefinitely" (CLAUDE.md section 8). The provider and certificate
 * number are the laboratory's own words. There is deliberately no pass/fail,
 * tolerance or measured-value field: whether a calibration is acceptable is
 * the provider's statement on the certificate (CLAUDE.md section 4).
 */
export const recordCalibrationSchema = z
    .object({
    calibratedOn: isoDateSchema,
    validUntil: isoDateSchema.nullable(),
    provider: optionalText(200),
    certificateNumber: optionalText(200),
    note: optionalText(2000),
})
    .strict()
    .refine((value) => value.validUntil === null || value.validUntil >= value.calibratedOn, {
    message: 'A calibration cannot expire before it was performed.',
    path: ['validUntil'],
});
/** The text part of a multipart certificate upload. */
export const calibrationCertificateMetadataSchema = z.object({
    note: optionalText(1000),
});
/** Removing a certificate marks the link removed, with a reason. The file is
 *  KEPT: a certificate is evidence, stored permanently. */
export const removeCertificateSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
/** Where an instrument was used, newest first. */
export const equipmentUsageQuerySchema = listQuerySchema.pick({
    page: true,
    pageSize: true,
});
// ---------------------------------------------------------------------------
// Specification register (D87)
// ---------------------------------------------------------------------------
/**
 * A specification a laboratory works to. The register ships EMPTY: which
 * standards a laboratory uses is its own policy, and which method a test
 * follows is not this product's claim to make.
 */
export const createSpecificationSchema = z
    .object({
    code: boundedText(1, 100),
    nameAr: optionalText(300),
    nameEn: optionalText(300),
    issuingBody: optionalText(200),
    notes: optionalText(2000),
})
    .strict();
export const updateSpecificationSchema = z
    .object({
    code: boundedText(1, 100).optional(),
    nameAr: optionalText(300),
    nameEn: optionalText(300),
    issuingBody: optionalText(200),
    notes: optionalText(2000),
    isActive: z.boolean().optional(),
})
    .strict();
export const specificationListQuerySchema = listQuerySchema.extend({
    includeInactive: booleanQuery,
});
// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------
/**
 * Assign a test to an accepted sample.
 *
 * `assignedTo` is optional: work can be queued for a department before a
 * person is chosen, and the unassigned list exists for exactly that.
 * `duplicateReason` is how a person confirms they really mean to assign the
 * same test to the same sample again, after the warning.
 */
export const createAssignmentSchema = z
    .object({
    sampleId: uuidSchema,
    pieceId: uuidSchema.nullable().optional(),
    catalogueItemId: uuidSchema,
    workOrderServiceId: uuidSchema.nullable().optional(),
    specificationId: uuidSchema.nullable().optional(),
    /** A project specification or anything not in the register, as written. */
    specificationText: optionalText(500),
    departmentId: uuidSchema.nullable().optional(),
    assignedTo: uuidSchema.nullable().optional(),
    priority: workOrderPrioritySchema.optional(),
    dueDate: isoDateSchema.nullable().optional(),
    instructions: optionalText(4000),
    duplicateReason: reasonSchema.optional(),
})
    .strict();
/** `GET /assignments/assignable-tests` (D316). */
export const assignableTestsQuerySchema = z.object({ sampleId: uuidSchema }).strict();
export const updateAssignmentSchema = z
    .object({
    priority: workOrderPrioritySchema.optional(),
    instructions: optionalText(4000),
    specificationId: uuidSchema.nullable().optional(),
    specificationText: optionalText(500),
})
    .strict();
export const setAssigneeSchema = z
    .object({
    assignedTo: uuidSchema,
    /** Required by the backend when the work is taken from somebody. */
    reason: reasonSchema.optional(),
})
    .strict();
export const transferDepartmentSchema = z
    .object({
    departmentId: uuidSchema,
    note: optionalText(2000),
})
    .strict();
export const recordDelaySchema = z
    .object({
    reason: reasonSchema,
    expectedDate: isoDateSchema,
})
    .strict();
export const stopAssignmentSchema = z
    .object({
    reason: reasonSchema,
    expectedDate: isoDateSchema.optional(),
})
    .strict();
export const resumeAssignmentSchema = z
    .object({
    note: optionalText(2000),
})
    .strict();
export const cancelAssignmentSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
export const assignmentListQuerySchema = listQuerySchema.extend({
    view: assignmentListViewSchema.default('all'),
    sampleId: uuidSchema.optional(),
    assignedTo: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    catalogueItemId: uuidSchema.optional(),
    /**
     * The lab work of one work order's samples, or `none` for the work of
     * samples linked to no work order (D326).
     */
    workOrderId: z.union([uuidSchema, z.literal('none')]).optional(),
    /**
     * What a page counts (D326). `assignment`, the default, pages the lab work
     * itself. `workOrder` pages WORK ORDERS: `page` and `pageSize` count groups,
     * newest work order first, and every group on a page carries its matching
     * lab work, its newest sample first, so a work order is never split across
     * two pages. The work of samples linked to no work order is one group,
     * always the last.
     */
    pageBy: z.enum(['assignment', 'workOrder']).default('assignment'),
    /**
     * With `pageBy=workOrder`: at most this many lab work items of each group.
     * The rest of a larger group is read with `workOrderId`, `sort=workOrder`
     * and the ordinary paging, which orders them the same way.
     */
    groupItemLimit: z.coerce.number().int().positive().max(100).default(50),
});
/**
 * My Tasks (D327). The person is always the session's; nothing here can name
 * another. `search` matches the Sample ID, the test and the work order.
 * `pageBy=workOrder` pages WORK ORDERS, each placed by its most pressing task
 * (the same priority-then-date rule as the flat list), each carrying at most
 * `groupItemLimit` of the person's open tasks.
 */
export const myTasksQuerySchema = listQuerySchema.extend({
    pageBy: z.enum(['assignment', 'workOrder']).default('assignment'),
    groupItemLimit: z.coerce.number().int().positive().max(100).default(100),
});
/**
 * The work list's work order filter (D326): this laboratory's work orders that
 * have lab work, newest first, matched on their number, client or project. A
 * short list per request, searched on the server.
 */
export const assignmentWorkOrderOptionsQuerySchema = z.object({
    search: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().positive().max(50).default(20),
});
// ---------------------------------------------------------------------------
// Attempts — the general path
// ---------------------------------------------------------------------------
export const startAttemptSchema = z
    .object({
    /** Required by the backend when a submitted attempt already exists. */
    retestReason: reasonSchema.optional(),
})
    .strict();
/**
 * Save the general result of a draft attempt.
 *
 * THIS IS THE WHOLE GENERAL RESULT (`GENERAL_RESULT_FIELDS`). A key not listed
 * here is refused, not ignored.
 */
export const updateGeneralResultSchema = z
    .object({
    summaryAr: optionalText(8000),
    summaryEn: optionalText(8000),
    generalNotes: optionalText(8000),
    executionStartedAt: isoTimestampSchema.nullable().optional(),
    executionCompletedAt: isoTimestampSchema.nullable().optional(),
})
    .strict()
    .refine((value) => !value.executionStartedAt ||
    !value.executionCompletedAt ||
    Date.parse(value.executionCompletedAt) >= Date.parse(value.executionStartedAt), {
    message: 'Work cannot be completed before it started.',
    path: ['executionCompletedAt'],
});
export const recordEquipmentUseSchema = z
    .object({
    equipmentId: uuidSchema,
    usedOn: isoDateSchema,
    /** Required, with the permission, when the equipment is blocked. */
    overrideReason: reasonSchema.optional(),
    /**
     * Which part the instrument plays in a specialised template (D243):
     * for example `balance` or `oven` for ASTM D2216 (`templateEquipmentRoles`).
     * Absent on the general path.
     */
    role: z
        .string()
        .refine((value) => ALL_EQUIPMENT_ROLES.includes(value), { message: 'Unknown equipment role.' })
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// ASTM D2216 (D237–D247) — the shape of a save. Readings are decimal TEXT,
// checked by the shared moisture component in the service, so a draft keeps
// exactly what was typed and every problem names its row and field.
// ---------------------------------------------------------------------------
/** A reading as typed. Blank is absent; the service decides what it means. */
const readingText = z
    .string()
    .max(32)
    .nullable()
    .optional()
    .transform((value) => value ?? null);
export const d2216DeterminationSchema = z
    .object({
    id: uuidSchema,
    containerNumber: optionalText(40).transform((value) => value ?? null),
    containerMass: readingText,
    containerWetMass: readingText,
    containerDryMass: readingText,
})
    .strict();
export const saveD2216ResultSchema = z
    .object({
    executionStartedAt: isoTimestampSchema.nullable().optional(),
    executionCompletedAt: isoTimestampSchema.nullable().optional(),
    testInformation: z
        .object({
        methodUsed: z.enum(D2216_METHODS).nullable().optional().transform((v) => v ?? null),
        ovenTemperature: optionalText(40).transform((value) => value ?? null),
        ovenStartedAt: isoTimestampSchema.nullable().optional().transform((value) => value ?? null),
        nominalMaximumSize: optionalText(40).transform((value) => value ?? null),
    })
        .strict(),
    determinations: z.array(d2216DeterminationSchema).max(50),
    environment: z
        .object({
        ambientTemperature: readingText,
        relativeHumidity: readingText,
    })
        .strict(),
    remarks: optionalText(4000).transform((value) => value ?? null),
})
    .strict()
    .refine((value) => new Set(value.determinations.map((row) => row.id)).size === value.determinations.length, { message: 'Each determination needs its own id.', path: ['determinations'] })
    .refine((value) => !value.executionStartedAt ||
    !value.executionCompletedAt ||
    Date.parse(value.executionCompletedAt) >= Date.parse(value.executionStartedAt), { message: 'Work cannot be completed before it started.', path: ['executionCompletedAt'] });
// ---------------------------------------------------------------------------
// ASTM D2726 and D2041 (D260–D266) — the same rules as D2216: readings are
// decimal TEXT, checked and calculated by the shared component in the
// service, so a draft keeps exactly what was typed.
// ---------------------------------------------------------------------------
const executionWindow = {
    executionStartedAt: isoTimestampSchema.nullable().optional(),
    executionCompletedAt: isoTimestampSchema.nullable().optional(),
};
const environmentSchema = z
    .object({ ambientTemperature: readingText, relativeHumidity: readingText })
    .strict();
const completedNotBeforeStart = (value) => !value.executionStartedAt ||
    !value.executionCompletedAt ||
    Date.parse(value.executionCompletedAt) >= Date.parse(value.executionStartedAt);
const uniqueIds = (rows) => new Set(rows.map((row) => row.id)).size === rows.length;
export const d2726SpecimenSchema = z
    .object({
    id: uuidSchema,
    specimenNumber: optionalText(40).transform((value) => value ?? null),
    dryMassInAir: readingText,
    ssdMassInAir: readingText,
    massInWater: readingText,
})
    .strict();
export const saveD2726ResultSchema = z
    .object({
    ...executionWindow,
    testInformation: z
        .object({ methodUsed: optionalText(80).transform((value) => value ?? null) })
        .strict(),
    // The submission refuses more than the workbook's three; a draft may hold
    // a few more while the technician sorts them out.
    specimens: z.array(d2726SpecimenSchema).max(20),
    environment: environmentSchema,
    remarks: optionalText(4000).transform((value) => value ?? null),
})
    .strict()
    .refine((value) => uniqueIds(value.specimens), {
    message: 'Each specimen needs its own id.',
    path: ['specimens'],
})
    .refine(completedNotBeforeStart, {
    message: 'Work cannot be completed before it started.',
    path: ['executionCompletedAt'],
});
export const d2041DeterminationSchema = z
    .object({
    id: uuidSchema,
    specimenNumber: optionalText(40).transform((value) => value ?? null),
    dryMassInAir: readingText,
    containerWaterMass: readingText,
    containerSampleWaterMass: readingText,
})
    .strict();
export const saveD2041ResultSchema = z
    .object({
    ...executionWindow,
    testInformation: z
        .object({
        containerType: optionalText(80).transform((value) => value ?? null),
        procedure: optionalText(80).transform((value) => value ?? null),
    })
        .strict(),
    determinations: z.array(d2041DeterminationSchema).max(20),
    environment: environmentSchema,
    remarks: optionalText(4000).transform((value) => value ?? null),
})
    .strict()
    .refine((value) => uniqueIds(value.determinations), {
    message: 'Each determination needs its own id.',
    path: ['determinations'],
})
    .refine(completedNotBeforeStart, {
    message: 'Work cannot be completed before it started.',
    path: ['executionCompletedAt'],
});
// ---------------------------------------------------------------------------
// AASHTO T85 and T96 (D267–D274) — the same rules again. Neither sheet records
// ambient conditions, so neither body carries an environment.
// ---------------------------------------------------------------------------
export const t85DeterminationSchema = z
    .object({
    id: uuidSchema,
    dryMassInAir: readingText,
    ssdMassInAir: readingText,
    massInWater: readingText,
})
    .strict();
export const saveT85ResultSchema = z
    .object({
    ...executionWindow,
    // The submission refuses more than the workbook's three.
    determinations: z.array(t85DeterminationSchema).max(20),
    remarks: optionalText(4000).transform((value) => value ?? null),
})
    .strict()
    .refine((value) => uniqueIds(value.determinations), {
    message: 'Each determination needs its own id.',
    path: ['determinations'],
})
    .refine(completedNotBeforeStart, {
    message: 'Work cannot be completed before it started.',
    path: ['executionCompletedAt'],
});
/** One mass per size fraction of the workbook's grading table; blank where unused. */
const t96GradingSchema = z
    .object(Object.fromEntries(T96_FRACTION_KEYS.map((key) => [key, readingText])))
    .strict();
export const saveT96ResultSchema = z
    .object({
    ...executionWindow,
    testInformation: z
        .object({
        gradingDesignation: optionalText(40).transform((value) => value ?? null),
        numberOfSpheres: readingText,
        sphereMass: readingText,
        revolutions: readingText,
    })
        .strict(),
    grading: t96GradingSchema,
    retainedMassNo12: readingText,
    remarks: optionalText(4000).transform((value) => value ?? null),
})
    .strict()
    .refine(completedNotBeforeStart, {
    message: 'Work cannot be completed before it started.',
    path: ['executionCompletedAt'],
});
export const removeEquipmentUseSchema = z
    .object({
    reason: reasonSchema.optional(),
})
    .strict();
/** The text parts of a multipart attempt attachment. */
export const attemptAttachmentMetadataSchema = z.object({
    purpose: attemptAttachmentPurposeSchema.default('other'),
    note: optionalText(1000),
});
/** Detaching evidence from a draft says why. The file itself is retained. */
export const removeAttachmentSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Sample attachments (carried from Phase 7, decision D80)
// ---------------------------------------------------------------------------
export const sampleAttachmentMetadataSchema = z.object({
    description: optionalText(1000),
});
export const removeSampleAttachmentSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
//# sourceMappingURL=execution.js.map